<script lang="ts">
  import { router } from './lib/router.svelte.ts';
  import { sitzung, hatModulZugang } from './lib/sitzung.svelte.ts';
  import Anmeldung from './seiten/Anmeldung.svelte';
  import Einladung from './seiten/Einladung.svelte';
  import KontoLoeschungWiderrufen from './seiten/KontoLoeschungWiderrufen.svelte';
  import Uebersicht from './seiten/Uebersicht.svelte';
  import Anfragen from './seiten/Anfragen.svelte';
  import Termine from './seiten/Termine.svelte';
  import Rechnungen from './seiten/Rechnungen.svelte';
  import MeineDaten from './seiten/MeineDaten.svelte';
  import Aktenschrank from './seiten/Aktenschrank.svelte';
  import Organisation from './seiten/Organisation.svelte';

  void sitzung.laden();

  const segmente = $derived(router.segmente);
  const bereich = $derived(segmente[0] ?? '');

  /**
   * Der Akten-Safe taucht nur auf, wenn er auch gebucht ist. Einen Reiter
   * anzubieten, hinter dem eine Anmeldemaske für ein Konto steht, das es nicht
   * gibt, wäre nur verwirrend. Zählt sowohl das alte Einzelpaket als auch ein
   * Safe-Paket-Vertrag, der das Modul mit abdeckt (Migration 038) — sonst bleibt
   * der Reiter für alle neueren Safe-Paket-Kunden unsichtbar.
   */
  const hatAktenschrank = $derived(hatModulZugang(sitzung.pakete, 'das'));

  /**
   * Mitglieder verwalten (Migration 054, Selbstbedienung 07.09.2026) — nur
   * der Inhaber der gerade gewählten Organisation. Serverseitig ebenso
   * gesperrt (`verlangeInhaber` in `routes/portal.ts`); hier nur, damit gar
   * nicht erst ein Reiter zu einem 403 verlockt.
   */
  const istInhaber = $derived(
    sitzung.organisationen.find((o) => o.kundeId === sitzung.gewaehlt)?.rolle === 'inhaber',
  );
</script>

{#if bereich === 'einladung' && segmente[1]}
  <!-- Vor jeder Anmeldeprüfung: Wer eingeladen wurde, hat noch kein Passwort. -->
  <Einladung token={segmente[1]} />
{:else if bereich === 'konto-loeschung-widerrufen' && segmente[1]}
  <!-- Ebenso vor der Anmeldeprüfung: Der Zugang ist zu diesem Zeitpunkt gerade gesperrt. -->
  <KontoLoeschungWiderrufen token={segmente[1]} />
{:else if !sitzung.geprueft}
  <p class="ladehinweis">Einen Moment …</p>
{:else if !sitzung.konto}
  <Anmeldung />
{:else}
  <header class="kopfleiste">
    <div class="innen">
      <span class="markenname"><span class="tb-wordmark">IT-Service Karsten Fischer</span> · Ihr Kundenbereich</span>
      <span class="wer">
        {#if sitzung.organisationen.length > 1}
          <!--
            Nur wer wirklich mehreren Organisationen angehört, sieht die
            Umschaltung. Für alle anderen wäre ein Auswahlfeld mit einem
            Eintrag eine Frage ohne Antwortmöglichkeit.
          -->
          <label class="orgwahl">
            <span class="versteckt">Organisation</span>
            <select
              disabled={sitzung.wechselt}
              value={sitzung.gewaehlt ?? ''}
              onchange={(e) => sitzung.organisationWechseln(e.currentTarget.value)}
            >
              {#each sitzung.organisationen as org (org.kundeId)}
                <option value={org.kundeId}>{org.anzeigename}</option>
              {/each}
            </select>
          </label>
        {:else}
          {sitzung.kunde?.anzeigename}
        {/if}
        <button type="button" class="abmelden" onclick={() => sitzung.abmelden()}>Abmelden</button>
      </span>
    </div>
  </header>

  <!--
    Fünf Punkte, ausgeschrieben, immer sichtbar. Kein Klappmenü: Ein Menü, das
    man erst öffnen muss, ist für einen Teil der Zielgruppe ein Menü, das es
    nicht gibt (Konzept 6.8).
  -->
  <nav class="hauptnavigation" aria-label="Bereiche">
    <div class="innen">
      <a href="#/" aria-current={bereich === '' ? 'page' : undefined}>Übersicht</a>
      <a href="#/anfragen" aria-current={bereich === 'anfragen' ? 'page' : undefined}>
        Meine Anfragen
      </a>
      <a href="#/termine" aria-current={bereich === 'termine' ? 'page' : undefined}>Termine</a>
      <a href="#/rechnungen" aria-current={bereich === 'rechnungen' ? 'page' : undefined}>
        Rechnungen
      </a>
      {#if hatAktenschrank}
        <a href="#/aktenschrank" aria-current={bereich === 'aktenschrank' ? 'page' : undefined}>
          Akten-Safe
        </a>
      {/if}
      {#if istInhaber}
        <a href="#/organisation" aria-current={bereich === 'organisation' ? 'page' : undefined}>
          Organisation
        </a>
      {/if}
      <a href="#/daten" aria-current={bereich === 'daten' ? 'page' : undefined}>Meine Daten</a>
      <!-- Anleitungen liegen auf der Website; von hier aus trotzdem ein Klick
           (Karsten, 04.09.2026). Neuer Reiter, damit die Anmeldung bleibt. -->
      <a href="https://it-karsten-fischer.de/hilfe/" target="_blank" rel="noopener">Anleitungen ↗</a>
    </div>
  </nav>

  <main>
    {#if bereich === ''}
      <Uebersicht />
    {:else if bereich === 'anfragen'}
      <Anfragen id={segmente[1]} />
    {:else if bereich === 'termine'}
      <Termine />
    {:else if bereich === 'rechnungen'}
      <Rechnungen />
    {:else if bereich === 'aktenschrank' && hatAktenschrank}
      <Aktenschrank />
    {:else if bereich === 'organisation' && istInhaber}
      <Organisation />
    {:else if bereich === 'daten'}
      <MeineDaten />
    {:else}
      <h1>Diese Seite gibt es nicht</h1>
      <p><a href="#/">Zurück zur Übersicht</a></p>
    {/if}

    <p class="fusszeile leise klein">
      Kommen Sie nicht weiter? Rufen Sie uns an — wir gehen es gemeinsam durch.
    </p>
  </main>
{/if}

<style>
  .ladehinweis {
    text-align: center;
    margin-top: 4rem;
    color: var(--text-leise);
  }

  .orgwahl select {
    /* Groß genug zum Treffen auf dem Handy, und sichtbar als Bedienelement —
       ein Auswahlfeld, das wie Text aussieht, findet niemand. */
    min-height: 44px;
    padding: 0.3rem 0.6rem;
    font-size: 0.95rem;
    font-family: inherit;
    color: #fff;
    background: rgba(255, 255, 255, 0.12);
    border: 1px solid rgba(255, 255, 255, 0.6);
    border-radius: 6px;
  }

  .orgwahl select:disabled {
    opacity: 0.6;
  }

  /* Die Beschriftung bleibt für Screenreader da, nimmt aber keinen Platz. */
  .versteckt {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  .abmelden {
    /* Kleiner als die übrigen Knöpfe, aber nicht kleiner als 44px – sonst
       trifft man ihn auf dem Handy nicht zuverlässig. */
    min-height: 44px;
    padding: 0.3rem 0.9rem;
    margin-left: 0.75rem;
    font-size: 0.9rem;
    background: transparent;
    border-color: rgba(255, 255, 255, 0.6);
    color: #fff;
  }

  .abmelden:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: #fff;
  }

  .fusszeile {
    margin-top: 3rem;
    text-align: center;
  }
</style>
